import { useState } from "react";
import type {
  OfferProduct,
  OfferBenefit,
  CardIcon,
  UpsellPageDTO,
} from "../../types/upsell.types";
import parse from "html-react-parser";
import {
  buildRedirectUrl,
  normalizeUpsellProduct,
  useUpsellSubmit,
} from "../../hooks/useUpsellSubmit";

type ProductPickerProps = {
  tr_heading: string;
  tr_subheading: string;
  offer_products: OfferProduct[];
  offer_benefits: OfferBenefit[];
  cards_icons: CardIcon[];
  tr_skip_offer_label: string;
  tr_cta_label: string;
  tr_processing_text: string;
  tr_subscribe_label: string;
  tr_sale_label: string;
  tr_one_time_purchase_label: string;
  tr_per_item_label: string;
  link_decline?: string;
  offer_products_has_subscription: boolean;
  submitData: UpsellPageDTO;
};

const CheckCircleIcon = () => (
  <svg
    width="20px"
    height="20px"
    viewBox="0 0 20 20"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    className="check-circle-icon"
  >
    <g id="check-circle" stroke="none" fill="none" fillRule="nonzero">
      <path
        d="M17.7584295,12.2950664 C17.3982652,12.5645249 17.2537435,13.0221866 17.3381045,13.4634733 C17.5193567,14.4045888 17.2631036,15.4160032 16.5560552,16.1680559 C15.8169864,16.9529787 14.7646338,17.266164 13.776422,17.0966752 C13.3014163,17.0154498 12.8303107,17.2481914 12.5592275,17.6465091 C12.0021808,18.4634422 11.0638896,19 10.000597,19 C9.01464519,19 8.13651471,18.5384195 7.57016795,17.8198967 C7.29126462,17.4661036 6.85453941,17.292716 6.4092141,17.3598757 C5.5045233,17.4949908 4.54825189,17.2302269 3.83018332,16.554659 C3.07861436,15.8470529 2.76141057,14.8536131 2.88563205,13.9022607 C2.94422675,13.4562954 2.77156869,13.0079909 2.39578621,12.7612055 C1.58249651,12.226987 1.03404996,11.3194612 1.00200958,10.2729575 C0.969977196,9.2263938 1.46295508,8.28761752 2.24264438,7.70493362 C2.60280868,7.43547506 2.7473304,6.97781337 2.6629694,6.53652674 C2.48171723,5.59541116 2.73797029,4.58399684 3.44501873,3.83194412 C4.18408755,3.04702134 5.2364401,2.733836 6.2246519,2.90332477 C6.69965756,2.98455022 7.17076319,2.75180856 7.44184642,2.35349094 C7.99889307,1.53655784 8.93718426,1 10.000477,1 C10.9864287,1 11.8645592,1.4615805 12.430906,2.18010335 C12.7098093,2.53389642 13.1465345,2.70728402 13.5918598,2.64012432 C14.4965506,2.50500915 15.452822,2.76977314 16.1708906,3.44534096 C16.9224595,4.15294711 17.2396633,5.14638687 17.1154418,6.09773935 C17.0568471,6.54370457 17.2295052,6.99200908 17.6052877,7.2387945 C18.4185774,7.77301304 18.9670239,8.68053877 18.9990643,9.72704249 C19.0310967,10.7736062 18.5381188,11.7123825 17.7584295,12.2950664 Z"
        fill="var(--primary)"
      />
      <path
        d="M13.2668987,8.53027223 C12.5701123,9.09188197 11.981328,9.71829283 11.4726739,10.4338923 C11.117313,10.9327913 10.8539277,11.4957946 10.5738197,12.0929405 C10.4702308,12.3163766 10.3638547,12.5398127 10.2546916,12.7632488 C10.0149971,13.2440313 9.58717032,13.5415591 9.06458062,13.5575852 C8.59843061,13.5575852 8.15179061,13.2893225 7.89258612,12.8482568 L6.57635685,10.5655849 C6.41819367,10.2868919 6.51354289,9.9328264 6.79027023,9.77124858 L7.2947435,9.48277907 C7.57244372,9.32287587 7.927176,9.41794911 8.08768627,9.69529888 L9.01859273,11.311843 C9.31403011,10.6847354 9.62061607,10.0374209 10.0561075,9.4256426 C10.6731978,8.55590301 11.4118851,7.77920188 12.2495907,7.11928003 C12.3703742,7.02428522 12.5250147,6.98336371 12.6769748,7.00618411 C12.828935,7.02900453 12.9647349,7.11354252 13.0522885,7.23982406 L13.3923202,7.71015479 C13.5769685,7.96726893 13.5170449,8.32959779 13.2668987,8.53027223"
        fill="#FFFFFF"
      />
    </g>
  </svg>
);

export const ProductPicker = ({
  tr_heading,
  tr_subheading,
  offer_products,
  offer_benefits,
  cards_icons,
  tr_skip_offer_label,
  tr_cta_label,
  tr_processing_text,
  tr_subscribe_label,
  tr_sale_label,
  tr_one_time_purchase_label,
  tr_per_item_label,
  link_decline,
  offer_products_has_subscription,
  submitData,
}: ProductPickerProps) => {
  const [purchaseType, setPurchaseType] = useState<"onetime" | "subscribe">(
    "subscribe",
  );

  const hasSubscription = offer_products_has_subscription;
  const paymentIcon = cards_icons?.[0]?.card_icon;
  const { isSubmitting, submitProducts } = useUpsellSubmit(submitData);
  const declineUrl =
    buildRedirectUrl(link_decline ?? "", window.orderId || "") || "#";

  return (
    <div className="content-medium" id="products_picker">
      <section className="limited-offer-section">
        <h2 className="limited-offer-title section-heading">
          {parse(tr_heading)}
        </h2>
        <div className="section-subtitle">{parse(tr_subheading)}</div>

        {hasSubscription && (
          <div className="purchase-selector">
            <button
              className={`selector-btn${purchaseType === "subscribe" ? " active" : ""}`}
              data-type="subscribe"
              onClick={() => setPurchaseType("subscribe")}
            >
              {tr_subscribe_label}
              {tr_sale_label && (
                <span className="selector-badge">{tr_sale_label}</span>
              )}
            </button>
            <button
              className={`selector-btn${purchaseType === "onetime" ? " active" : ""}`}
              data-type="onetime"
              onClick={() => setPurchaseType("onetime")}
            >
              {tr_one_time_purchase_label}
            </button>
          </div>
        )}

        <div id="product-options" className="product-options">
          {offer_products.map((product, idx) => {
            const isSubscribe = purchaseType === "subscribe" && hasSubscription;
            const price = isSubscribe ? product.subscr_price : product.price;
            const oldPrice = isSubscribe
              ? product.subscr_old_price
              : product.old_price;
            const discountPercent = isSubscribe
              ? product.subscr_discount_percent
              : product.discount_percent;
            const productImage = isSubscribe
              ? product.use_different_product_image
                ? (product.subscr_product_image ?? null)
                : (product.subscr_product_image ?? product.product_image)
              : product.product_image;
            const statusLabel = product.tr_product_status_label;

            const isPopular = idx === 1;
            const cardClass = isPopular ? "option-card popular" : "option-card";

            return (
              <div key={product.sku + idx} className={cardClass}>
                {discountPercent && (
                  <div
                    className={`popularity-badge${isPopular ? " most-popular" : ""}`}
                  >
                    {statusLabel}
                  </div>
                )}

                {productImage && (
                  <div className="option-image">
                    <img src={productImage} alt={product.product_name} />
                  </div>
                )}

                <div className="option-pricing">
                  <div className="price-main">
                    {price?.formatted}{" "}
                    <span className="price-unit">{tr_per_item_label}</span>
                  </div>
                  {oldPrice?.value && (
                    <div className="price-old">{oldPrice.formatted}</div>
                  )}
                </div>

                {product.item_details && product.item_details.length > 0 && (
                  <div className="option-features">
                    {product.item_details.map((detail, i) => (
                      <div className="feature-item" key={i}>
                        <CheckCircleIcon />
                        <span>{detail.tr_detail}</span>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  className="order-btn add-to-order-btn"
                  onClick={() => {
                    const normalizedProduct = normalizeUpsellProduct(
                      product,
                      purchaseType,
                      hasSubscription,
                      { is_primary: true },
                    );

                    submitProducts([normalizedProduct], normalizedProduct);
                  }}
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting
                    ? tr_processing_text || "Processing"
                    : tr_cta_label}
                </button>
              </div>
            );
          })}
        </div>

        {offer_benefits.length > 0 && (
          <div className="trust-indicators">
            {offer_benefits.map((benefit, idx) => (
              <div className="trust-item" key={idx}>
                {benefit.icon && (
                  <img src={benefit.icon} alt={benefit.tr_benefit} />
                )}
                <span>{benefit.tr_benefit}</span>
              </div>
            ))}
          </div>
        )}

        {paymentIcon && (
          <div className="payment-security">
            <div className="payment-icons">
              <img src={paymentIcon} alt="Payment methods" />
            </div>
          </div>
        )}

        <div className="decline-container">
          <a href={declineUrl} className="decline-btn">
            {tr_skip_offer_label}
          </a>
        </div>
      </section>
    </div>
  );
};
